import './index.css';
import React, { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';

import { PrintLayoutApp } from './print-layout-app';

const searchParams = new URLSearchParams(window.location.search);
const ssrOnly = searchParams.get('ssrOnly') === 'true';
const forceRender = searchParams.get('forceRender') === 'true';

let imagesReady = false;
let fontsReady = false;
let isTimedOut = false;
let embedsRegisteredAt: number | null = null;

const DEFAULT_TIMEOUT_MS = 6000;
const EMBED_TIMEOUT_MS = 15000;
const EMBED_GRACE_MS = 3000;

// Quiet window required after the last DOM mutation before considering images ready.
// Covers ProseMirror node views, lazy plugin chunks, and async <wow-image> upgrades
// that materialize their inner <img> elements after the initial React commit.
const DOM_STABILITY_MS = 300;

// Cap on waiting for the wix-image custom element to register. Only used when the
// document actually contains <wow-image> instances; we skip the wait entirely for
// image-less docs so they don't pay this cost.
const CUSTOM_ELEMENT_DEFINE_TIMEOUT_MS = 1500;

// The @wix/image package registers its custom element under the tag name
// "wow-image" (see node_modules/@wix/image/.../custom-element/registry.js).
const WIX_IMAGE_TAG_NAME = 'wow-image';

const hasEmbeds = (window.viewerModel.htmlPluginsCount ?? 0) > 0;

const sketchesReady = (): boolean => window.ricosSketchesReady !== false;

setTimeout(
  () => {
    isTimedOut = true;
  },
  hasEmbeds ? EMBED_TIMEOUT_MS : DEFAULT_TIMEOUT_MS
);

// The width of the print content container (see PrintLayoutApp's wrapper div).
const PRINT_CONTENT_WIDTH_PX = 900;

const FLUID_PAGE_SIZE_STYLE_ID = 'ricos-fluid-page-size';

// Fluid pages must come out exactly content-sized, but the PDF capturer cannot
// derive that height itself: Gotenberg's singlePage mode measures the document's
// layout metrics, which never go below the viewport height, so short pages would
// be padded with blank space. Instead we measure the content ourselves and emit
// an exact @page size — the capturer passes preferCssPageSize, so this rule wins
// over its measured paper size. body.scrollHeight equals the content height
// because print mode releases all viewport-height floors (PRINT_HEIGHT_CSS in
// PrintLayoutApp, and the card plugin's print class). Runs at most once, right
// before printReady() first returns true, so the capturer always prints with the
// rule in place.
const injectFluidPageSize = (): void => {
  const isFluid =
    !window.viewerModel.pageFormat || window.viewerModel.pageFormat.toUpperCase() === 'FLUID';
  if (!window.viewerModel.isPrint || !isFluid) {
    return;
  }
  if (document.getElementById(FLUID_PAGE_SIZE_STYLE_ID)) {
    return;
  }
  const contentHeight = Math.ceil(document.body.scrollHeight);
  const style = document.createElement('style');
  style.id = FLUID_PAGE_SIZE_STYLE_ID;
  style.textContent = `@page { size: ${PRINT_CONTENT_WIDTH_PX}px ${contentHeight}px; margin: 0; }`;
  document.head.appendChild(style);
};

// @ts-expect-error
window.printReady = () => {
  if (isTimedOut) {
    injectFluidPageSize();
    return true;
  }

  const htmlPluginsCount: number = window.viewerModel.htmlPluginsCount ?? 0;
  // @ts-expect-error window.ricosHtmlPlugins is set by plugin runtime, not in viewerModel typings
  const loadedCount: number = window.ricosHtmlPlugins?.size ?? 0;

  // Track when all embeds have registered (independently of images/fonts)
  if (htmlPluginsCount > 0 && htmlPluginsCount === loadedCount && embedsRegisteredAt === null) {
    embedsRegisteredAt = Date.now();
  }

  const embedsReady =
    htmlPluginsCount === 0 ||
    (embedsRegisteredAt !== null && Date.now() - embedsRegisteredAt >= EMBED_GRACE_MS);

  const ready = imagesReady && fontsReady && embedsReady && sketchesReady();
  if (ready) {
    injectFluidPageSize();
  }
  return ready;
};

const waitForImages = async (images: HTMLImageElement[]): Promise<void> => {
  if (images.length === 0) {
    return;
  }

  await Promise.all(
    images.map(img =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>(resolve => {
            const done = () => {
              img.removeEventListener('load', done);
              img.removeEventListener('error', done);
              resolve();
            };
            img.addEventListener('load', done);
            img.addEventListener('error', done);
          })
    )
  );
};

// Wait for a custom element to register, but only when the document actually
// contains instances of it. Pages with no <wow-image> never trigger registration
// (pluginImage is lazy-loaded), so an unconditional wait would add latency to
// every image-less PDF capture for no benefit.
const waitForCustomElement = async (name: string): Promise<void> => {
  if (typeof window.customElements?.whenDefined !== 'function') {
    return;
  }
  if (document.querySelector(name) === null) {
    return;
  }
  await Promise.race([
    window.customElements.whenDefined(name).then(() => undefined),
    new Promise<void>(resolve => {
      window.setTimeout(resolve, CUSTOM_ELEMENT_DEFINE_TIMEOUT_MS);
    }),
  ]);
};

const nodeContainsImg = (node: Node): boolean =>
  node.nodeName === 'IMG' || (node instanceof Element && node.querySelector('img') !== null);

// Decide whether a mutation could change image readiness.
//   - Attribute mutations only matter when the target is an <img>; src/srcset
//     also exist on <iframe>, <script>, <video>, <source>, <embed>, etc., and
//     those don't represent image loads we can observe via HTMLImageElement.
//   - childList mutations only matter when an <img> is added or removed
//     (directly, or anywhere in the inserted/removed subtree).
// Every other mutation is unrelated DOM churn (animated embeds/widgets, text
// updates, decoration nodes) and must not reset the stability timer.
const isImageMutation = (mutation: MutationRecord): boolean => {
  if (mutation.type === 'attributes') {
    return mutation.target.nodeName === 'IMG';
  }
  if (mutation.type !== 'childList') {
    return false;
  }
  for (const node of mutation.addedNodes) {
    if (nodeContainsImg(node)) {
      return true;
    }
  }
  for (const node of mutation.removedNodes) {
    if (nodeContainsImg(node)) {
      return true;
    }
  }
  return false;
};

// Idempotency guard. React StrictMode (and HMR) double-invoke effects, which
// would otherwise spin up two MutationObservers + two stability timers racing
// to flip the same flag.
let imagesReadyTracked = false;

// Track readiness reactively instead of snapshotting on mount: wait for the
// <wow-image> custom element to register, then observe DOM mutations until the
// document has been quiescent for DOM_STABILITY_MS with all <img>s complete.
//
// This catches four races that the previous one-shot approach missed:
//   1. <wow-image> hasn't upgraded yet → its inner <img> doesn't exist yet, so
//      the snapshot found zero images and resolved instantly.
//   2. ProseMirror node views (tables, layouts) mount images after the first
//      React effect runs.
//   3. Plugins lazy-load chunks that swap LQIP src → full src after mount.
//   4. Lazy plugin chunks load over the network and only render their <img>s
//      after a quiet window has already elapsed; without keeping the observer
//      alive past the first settle, those late images would be missed.
const trackImagesReady = async (): Promise<void> => {
  if (imagesReadyTracked) {
    return;
  }
  imagesReadyTracked = true;

  await waitForCustomElement(WIX_IMAGE_TAG_NAME);

  let stabilityTimer: number | null = null;
  let observer: MutationObserver | null = null;

  // settle() schedules (or re-schedules) the stability check. It clears any
  // in-flight timer and re-arms imagesReady=false, so callers can be confident
  // that after settle() runs, the readiness signal can only flip true again
  // through the normal path (timer fires → images all complete → rAF×2).
  //
  // Concurrency: the existence of `stabilityTimer` is used as the "newer settle
  // is in flight" gate. The async timer callback nulls it before doing async
  // work; if any later step finds it non-null, a fresh settle has been
  // scheduled and the old branch must abort to avoid stamping a stale
  // imagesReady=true.
  const settle = () => {
    if (stabilityTimer !== null) {
      window.clearTimeout(stabilityTimer);
    }
    imagesReady = false;
    stabilityTimer = window.setTimeout(async () => {
      stabilityTimer = null;
      const images = Array.from(document.querySelectorAll<HTMLImageElement>('img'));
      const incomplete = images.filter(img => !img.complete);
      if (incomplete.length > 0) {
        await waitForImages(incomplete);
        if (stabilityTimer !== null) {
          return;
        }
        settle();
        return;
      }
      // rAF×2 mirrors the fonts-ready flush: ensures the browser has committed
      // a paint with the loaded images before the capturer takes its snapshot.
      requestAnimationFrame(() => {
        if (stabilityTimer !== null) {
          return;
        }
        requestAnimationFrame(() => {
          if (stabilityTimer !== null) {
            return;
          }
          imagesReady = true;
        });
      });
    }, DOM_STABILITY_MS);
  };

  observer = new MutationObserver(mutations => {
    // Stop observing once the global ceiling has fired — printReady() already
    // returns true at that point, so further work is wasted.
    if (isTimedOut) {
      observer?.disconnect();
      return;
    }
    if (mutations.some(isImageMutation)) {
      settle();
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'srcset'],
  });

  settle();
};

if (!ssrOnly) {
  const HydrationDetector: React.FC<React.PropsWithChildren> = ({ children }) => {
    React.useEffect(() => {
      trackImagesReady();
      document.fonts.ready.then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fontsReady = true;
          });
        });
      });
    }, []);
    return children;
  };

  const component = (
    <StrictMode>
      <HydrationDetector>
        <PrintLayoutApp
          content={JSON.parse(decodeURIComponent(atob(window.viewerModel.content)))}
          design={JSON.parse(decodeURIComponent(atob(window.viewerModel.design)))}
          title={JSON.parse(decodeURIComponent(atob(window.viewerModel.title)))}
          isPrint={window.viewerModel.isPrint}
          instance={window.viewerModel.instance}
          pageFormat={window.viewerModel.pageFormat}
          isPagesEnabled={window.viewerModel.isPagesEnabled}
        />
      </HydrationDetector>
    </StrictMode>
  );

  // Skip hydration in print mode: lazy-plugin Suspense boundaries can be
  // interrupted mid-hydration and force a client-side rebuild that briefly
  // empties the DOM, which the PDF capturer can snapshot. createRoot has no
  // dehydrated state and avoids the race entirely.
  const skipHydration = forceRender || window.viewerModel.isPrint;

  // On any React recovery (hydration mismatch, Suspense restart) reset
  // imagesReady so the MutationObserver/settle pipeline waits for the rebuilt
  // tree before printReady() can return true again.
  const onRecoverableError = (error: unknown): void => {
    imagesReady = false;
    console.warn('[ricos-www-viewer] React recoverable error', error);
  };

  if (skipHydration) {
    const root = createRoot(document.getElementById('root') as HTMLElement, {
      onRecoverableError,
    });
    root.render(component);
  } else {
    hydrateRoot(document.getElementById('root') as HTMLElement, component, {
      onRecoverableError,
    });
  }
}
